// =============================================================================
// Pintravel — Application Entry Point
// =============================================================================
// Bootstraps the React application with all required providers.
// On native platforms (iOS/Android), Capacitor plugins are initialized here.
// =============================================================================

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from '@/app/providers';
import { router } from '@/app/router';
import { RouterProvider } from 'react-router-dom';

import { initializeDeepLinks } from '@/lib/deep-links';

// ---------------------------------------------------------------------------
// Fonts + Global Styles
// ---------------------------------------------------------------------------
// Poppins is the single brand family (theme references it as fontFamily).
// Self-hosted via @fontsource so native builds have no network dependency —
// only the weights the design actually uses are bundled.
// ---------------------------------------------------------------------------
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';

import './index.css';

// ---------------------------------------------------------------------------
// i18n Initialization
// ---------------------------------------------------------------------------
// Side-effect import — initializes i18next with English and Polish translations.
// Must be imported before any component that uses useTranslation().
// ---------------------------------------------------------------------------
import '@/i18n/i18n';

// ---------------------------------------------------------------------------
// Capacitor Initialization (native platforms only)
// ---------------------------------------------------------------------------
// On iOS/Android, we need to signal that the web app has finished loading
// so the native splash screen can be hidden. We also set up the status bar
// and keyboard behavior.
// ---------------------------------------------------------------------------

async function initializeCapacitor(): Promise<void> {
  try {
    // Dynamic import — these modules are only meaningful on native platforms.
    // On web, they are no-ops or gracefully handle the absence of native APIs.
    const { Capacitor } = await import('@capacitor/core');

    if (Capacitor.isNativePlatform()) {
      // Hide the splash screen after the app has rendered
      const { SplashScreen } = await import('@capacitor/splash-screen');
      await SplashScreen.hide({ fadeOutDuration: 300 });

      // System bar styles are handled reactively by useNativeSystemBars hook
      // (syncs with theme changes: light theme → dark icons, dark theme → light icons)

      // Set up keyboard behavior
      // Use Capacitor keyboard events which fire at ANIMATION START
      // This allows CSS transitions to animate in sync with keyboard
      const { Keyboard } = await import('@capacitor/keyboard');

      // Store the full viewport height (without keyboard)
      let fullHeight = window.innerHeight;

      // Set initial height
      document.documentElement.style.setProperty('--app-height', `${fullHeight}px`);

      // keyboardWillShow fires at the START of keyboard animation with the keyboard height
      // This lets us calculate target height immediately so CSS transition syncs with keyboard
      Keyboard.addListener('keyboardWillShow', (info) => {
        const newHeight = fullHeight - info.keyboardHeight;
        document.documentElement.style.setProperty('--app-height', `${newHeight}px`);
        document.body.classList.add('keyboard-open');
      });

      // keyboardWillHide fires at the START of hide animation
      Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--app-height', `${fullHeight}px`);
        document.body.classList.remove('keyboard-open');
      });

      // Also listen to Visual Viewport for orientation changes and initial setup
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
          const vpHeight = window.visualViewport!.height;
          const isKeyboardOpen = document.body.classList.contains('keyboard-open');
          // Grow only: WebKit can report the keyboard-shrunk viewport before
          // keyboardWillShow arrives over the bridge, and taking that as
          // fullHeight subtracts the keyboard twice. Shrinking is left to
          // orientationchange.
          if (!isKeyboardOpen && vpHeight > fullHeight) {
            fullHeight = vpHeight;
            document.documentElement.style.setProperty('--app-height', `${fullHeight}px`);
          }
        });
      }

      // Handle orientation changes
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          fullHeight = window.innerHeight;
          if (!document.body.classList.contains('keyboard-open')) {
            document.documentElement.style.setProperty('--app-height', `${fullHeight}px`);
          }
        }, 100);
      });

      // Listening only — no permission prompt and no token yet, both of which
      // belong to a signed-in session. This goes up now because a notification
      // that *launches* the app delivers its tap almost immediately, and with
      // nothing listening the destination is simply lost.
      const { listenForPushTaps } = await import('@/lib/push/push');
      await listenForPushTaps();

      console.log('[Pintravel] Capacitor initialized on', Capacitor.getPlatform());
    }
  } catch {
    // Capacitor plugins not available — running in browser mode
    console.debug('[Pintravel] Running in browser mode (Capacitor not available)');
  }
}

// ---------------------------------------------------------------------------
// Mount the React Application
// ---------------------------------------------------------------------------

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure there is a <div id="root"></div> in your index.html.',
  );
}

const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);

// Initialize Capacitor after the first render
initializeCapacitor();

// Universal links. Registered after the router exists, because a link that
// arrives while the app is cold is delivered the moment the listener is added.
initializeDeepLinks();

// ---------------------------------------------------------------------------
// Hot Module Replacement (development only)
// ---------------------------------------------------------------------------

if (import.meta.hot) {
  import.meta.hot.accept();
}
